import {
  CHECKERS_PER_SIDE,
  OFF,
  Position,
  Side,
  hashPosition,
  mirror,
  startingPosition,
} from "../engine/position";
import { Play, SubMove, applyPlay, generatePlays } from "../engine/moves";
import { GameResult, checkWin, rollDice, rollOpening } from "../engine/rules";
import { applyDoubleAccepted, canDouble } from "../engine/cube";
import { AIClient, MoveAnalysis } from "../ai/api";
import { Difficulty } from "../ai/levels";
import { deserializeGame, saveGame, saveSettings, serializeGame } from "./persistence";

export type PlayerKind = "human" | "cpu";

// Coaching mode:
//  - "off":     no analysis, no flags, no report.
//  - "tutor":   analyze every human move and pop an error/blunder flag on the
//               turn it happens (gating the next roll); plus an end-of-game report.
//  - "trainer": analyze silently and only reveal the net play rating (PR)
//               in the end-of-game report — no per-turn interruptions.
export type TutorMode = "off" | "tutor" | "trainer";

export interface GameSettings {
  matchLength: number;
  cubeEnabled: boolean;
  whitePlayer: PlayerKind;
  blackPlayer: PlayerKind;
  whiteName: string;
  blackName: string;
  cpuDifficulty: Difficulty;
  tutorMode: TutorMode;
  showPipCount: boolean;
  showEquity: boolean;
  soundEnabled: boolean;
  // When false, a human player's dice are not rolled automatically — the board
  // waits in the "roll" phase (state.awaitingRoll) for a tap. The computer
  // always rolls itself either way.
  autoRoll: boolean;
}

export interface TutorEntry {
  side: Side;
  // Equity loss vs the best play, in gammon-aware points equity (EMG scale).
  // 0 = perfect play.
  equityLoss: number;
  classification: "good" | "doubtful" | "error" | "blunder";
  bestPlay: Play;
  yourPlay: Play;
  // True when the roll left no real choice — one legal play, or several that
  // all reach the same position. Such a turn is not a decision: it can only
  // ever score 0 loss, so the performance rating excludes it from its average
  // (counting them dragged every rating toward a flattering 0).
  forced: boolean;
  // Optional human-readable note
  note?: string;
  // Pre-move position, all legal plays analyzed, and the corresponding
  // equity vectors — needed so the analysis modal can paint the pre-move
  // board and render the full ranked list of alternatives (ranked by points
  // equity, with Win% derived from game equity).
  startPos: Position;
  legalPlays: Play[];
  pointsEquities: number[];
  gameEquities: number[];
}

export type Phase =
  | { kind: "menu" }
  | { kind: "opening"; whiteDie: number; blackDie: number; firstSide: Side }
  | { kind: "roll" }
  | { kind: "play"; legalPlays: Play[] }
  | { kind: "cpu-thinking" }
  | { kind: "cube-decision"; doubler: Side }
  | { kind: "won"; result: GameResult }
  | { kind: "match-won"; winner: Side };

export interface State {
  position: Position; // canonical: always in "us" (player on roll) perspective
  phase: Phase;
  pendingPlay: Play; // sub-moves chosen so far this turn (us-coords)
  selectedFrom: number | null;
  // True while a human player's roll is being held for a tap (auto-roll off).
  awaitingRoll: boolean;
  settings: GameSettings;
  // Match-level state (also embedded in position.score, but kept here for convenience)
  whiteScore: number;
  blackScore: number;
  gameNumber: number;
  // True once the Crawford game has been played this match (so subsequent
  // games are post-Crawford and the cube is live again).
  crawfordPlayed: boolean;
  // Tutor (Phase 4)
  tutor: {
    history: TutorEntry[];
    lastEntry?: TutorEntry;
  };
  // Absolute White POV equity of the position RESULTING from the last commit.
  currentEquity: number | null;
}

function emptyState(settings: GameSettings): State {
  return {
    position: startingPosition({ matchLength: settings.matchLength }),
    phase: { kind: "menu" },
    pendingPlay: [],
    selectedFrom: null,
    awaitingRoll: false,
    settings,
    whiteScore: 0,
    blackScore: 0,
    gameNumber: 1,
    crawfordPlayed: false,
    tutor: { history: [] },
    currentEquity: null,
  };
}

// Compute the working position with `pendingPlay` applied (for rendering during human's turn).
export function workingPosition(state: State): Position {
  if (state.pendingPlay.length === 0) return state.position;
  return applyPlay(state.position, state.pendingPlay);
}

// Plays (from `legalPlays`) that still match `pendingPlay` as a prefix.
export function remainingPlays(legalPlays: Play[], pendingPlay: Play): Play[] {
  return legalPlays.filter((pl) => playStartsWith(pl, pendingPlay));
}

function playStartsWith(play: Play, prefix: Play): boolean {
  if (prefix.length > play.length) return false;
  for (let i = 0; i < prefix.length; i++) {
    const a = play[i];
    const b = prefix[i];
    if (a.from !== b.from || a.to !== b.to || a.die !== b.die) return false;
  }
  return true;
}

// Map of "next sub-move from" -> set of legal "to" destinations, given pendingPlay.
export function legalNextTargets(legalPlays: Play[], pendingPlay: Play): Map<number, Set<number>> {
  const m = new Map<number, Set<number>>();
  const remaining = remainingPlays(legalPlays, pendingPlay);
  for (const play of remaining) {
    if (play.length <= pendingPlay.length) continue;
    const sub = play[pendingPlay.length];
    let s = m.get(sub.from);
    if (!s) {
      s = new Set();
      m.set(sub.from, s);
    }
    s.add(sub.to);
  }
  return m;
}

export function isPendingComplete(legalPlays: Play[], pendingPlay: Play): boolean {
  const remaining = remainingPlays(legalPlays, pendingPlay);
  return remaining.length > 0 && remaining.every((p) => p.length === pendingPlay.length);
}

export function consumedDice(pendingPlay: Play): number[] {
  return pendingPlay.map((s) => s.die);
}

export function remainingDice(all: number[], pendingPlay: Play): number[] {
  const r = [...all];
  for (const sub of pendingPlay) {
    const i = r.indexOf(sub.die);
    if (i >= 0) r.splice(i, 1);
  }
  return r;
}

export type Listener = () => void;

// Everything the post-commit tutor analysis needs, captured at commit time so
// it survives the state mutations that follow (including endGame, which clears
// the controller's active analysis).
interface TutorCtx {
  onRoll: Side;
  yourPlay: Play;
  legalPlays: Play[];
  startPos: Position;
  analysis: Promise<MoveAnalysis>;
}

/** True when `legalPlays` offers no real choice: every option lands on the
 * same position (or there is nothing to play at all). */
function isForcedTurn(startPos: Position, legalPlays: Play[]): boolean {
  if (legalPlays.length <= 1) return true;
  const first = hashPosition(applyPlay(startPos, legalPlays[0]));
  for (let i = 1; i < legalPlays.length; i++) {
    if (hashPosition(applyPlay(startPos, legalPlays[i])) !== first) return false;
  }
  return true;
}

export class GameController {
  state: State;
  ai: AIClient;
  private listeners = new Set<Listener>();
  // True while we're waiting on the user to dismiss an error/blunder tutor
  // modal. The next dice roll is held off until ackTutor() is called.
  private tutorAckPending = false;

  // Eager turn analysis: kicked off the moment dice are resolved. Bundles the
  // analyze() promise with the exact legalPlays array it was given so commit-time
  // and tutor lookups can match the committed play against the same row vector
  // without depending on phase state or rebuilding plays.
  private activeAnalysis: { promise: Promise<MoveAnalysis>; legalPlays: Play[] } | null = null;

  // Bumped whenever the board's identity changes (new game, resume, back to
  // menu). Async analysis callbacks capture it and bail if it moved on, so a
  // slow resolve from a dead game can never paint equity onto a fresh board
  // or inject a tutor entry into the next game. Deliberately NOT bumped in
  // endGame: the final play's equity legitimately lands on the won screen.
  private analysisEpoch = 0;

  constructor(settings: GameSettings, ai: AIClient) {
    this.ai = ai;
    this.state = emptyState(settings);
  }

  /** Retrieve the eager analysis promise for the current turn. */
  public getActiveAnalysis(): Promise<MoveAnalysis> | null {
    return this.activeAnalysis?.promise ?? null;
  }

  private triggerEagerAnalysis(p: Position, legalPlays: Play[]): void {
    const matchLen = this.state.settings.matchLength;
    const usScore = p.turn === 0 ? this.state.whiteScore : this.state.blackScore;
    const themScore = p.turn === 0 ? this.state.blackScore : this.state.whiteScore;
    const promise = this.ai.analyze(
      p,
      legalPlays,
      this.state.settings.cpuDifficulty,
      matchLen - usScore,
      matchLen - themScore,
    );
    this.activeAnalysis = { promise, legalPlays };

    // The equity readout tracks the BEST play available to the player on roll
    // — the same number the hint modal's #1 row prints, in the same points-
    // equity metric. It lands as soon as this turn's eager analysis resolves.
    // (It used to report the game equity of the play actually committed, which
    // answered a different question and never matched the hint list.)
    const forfeit = legalPlays.length === 1 && legalPlays[0].length === 0;
    const onRoll = p.turn;
    const epoch = this.analysisEpoch;
    void promise
      .then((a) => {
        // Stale resolve (previous game/menu), or a newer roll/dice-swap already
        // owns the readout — either way this equity no longer describes the board.
        if (epoch !== this.analysisEpoch) return;
        if (this.activeAnalysis?.promise !== promise) return;
        // A forfeit has exactly one "play" (do nothing) and scores a flat 0,
        // which would read as a dead-even game. Keep the previous number.
        if (forfeit || !Number.isFinite(a.bestEquity)) return;
        this.state.currentEquity = onRoll === 0 ? a.bestEquity : -a.bestEquity;
        this.emit();
      })
      .catch(() => {
        // analysis failures leave the previous readout in place
      });
  }

  /**
   * Hand the dice to the next player: roll after `delayMs`, or — when auto-roll
   * is off and a human is on roll — park in the "roll" phase until rollDice()
   * is called by a tap.
   */
  private scheduleRoll(delayMs: number): void {
    if (this.state.phase.kind !== "roll") return;
    const turn = this.state.position.turn;
    const nextIsHuman =
      turn === 0 ? this.state.settings.whitePlayer === "human" : this.state.settings.blackPlayer === "human";
    if (this.state.settings.autoRoll || !nextIsHuman) {
      setTimeout(() => this.rollDice(), delayMs);
      return;
    }
    this.state.awaitingRoll = true;
    this.emit();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  setSettings(s: GameSettings): void {
    const wasAwaitingRoll = this.state.awaitingRoll;
    this.state.settings = s;
    void saveSettings(s);
    this.emit();
    // Switching auto-roll back on while the board is parked waiting for a tap
    // has to release it — otherwise the game sits there with no cue left.
    if (wasAwaitingRoll && s.autoRoll) this.scheduleRoll(100);
  }

  /** Start a brand-new match. */
  startNewMatch(): void {
    const s = this.state.settings;
    this.state = emptyState(s);
    this.startNewGame();
  }

  /** Start a new game within the current match (or a single game). */
  startNewGame(): void {
    this.analysisEpoch++;
    this.activeAnalysis = null;
    this.state.currentEquity = null;
    this.state.position = startingPosition({ matchLength: this.state.settings.matchLength });
    this.state.position.score = [this.state.whiteScore, this.state.blackScore];
    // Crawford rule: the game right AFTER one side first reaches
    // matchLength - 1 points is the Crawford game (no cube allowed). The
    // following games are post-Crawford (cube live again for the trailer).
    if (
      this.state.settings.matchLength > 1
      && !this.state.crawfordPlayed
      && Math.max(this.state.whiteScore, this.state.blackScore)
         === this.state.settings.matchLength - 1
    ) {
      this.state.position.crawford = true;
    } else {
      this.state.position.crawford = false;
    }
    this.state.pendingPlay = [];
    this.state.selectedFrom = null;
    this.state.awaitingRoll = false;
    // Opening roll
    const [d1, d2] = rollOpening();
    const firstSide: Side = d1 > d2 ? 0 : 1;
    if (firstSide === 1) this.state.position = mirror(this.state.position);
    // Use both dice as the first roll
    this.state.position.dice = [d1, d2];
    this.state.position.turn = firstSide;
    this.state.phase = {
      kind: "opening",
      whiteDie: d1,
      blackDie: d2,
      firstSide,
    };
    this.emit();
    // Checkpoint at the game boundary so a reload lands back in this game
    // (the opening winner is preserved; the dice are simply re-rolled).
    this.saveCheckpoint();
    // Pause briefly to show the opening dice, then start the first turn.
    setTimeout(() => this.afterRoll(), 1300);
  }

  /** Persist a resumable snapshot. Called at turn boundaries (dice unrolled). */
  private saveCheckpoint(): void {
    void saveGame(
      serializeGame({
        position: this.state.position,
        whiteScore: this.state.whiteScore,
        blackScore: this.state.blackScore,
        gameNumber: this.state.gameNumber,
        crawfordPlayed: this.state.crawfordPlayed,
      }),
    );
  }

  /**
   * Restore a checkpointed game (see saveCheckpoint). Returns false when the
   * blob is missing/invalid — caller falls back to the menu. On success the
   * game re-enters at "next player to roll".
   */
  resumeGame(blob: unknown): boolean {
    // Only resume onto an idle board. The boot-time IDB read is async, and a
    // tap on the idle board starts a match immediately — without this guard a
    // slow read could land afterward and discard the game just started.
    if (this.state.phase.kind !== "menu") return false;
    const parsed = deserializeGame(blob);
    if (!parsed) return false;
    this.analysisEpoch++;
    this.activeAnalysis = null;
    this.state = emptyState(this.state.settings);
    this.state.position = parsed.position;
    this.state.whiteScore = parsed.saved.whiteScore;
    this.state.blackScore = parsed.saved.blackScore;
    this.state.gameNumber = parsed.saved.gameNumber;
    this.state.crawfordPlayed = parsed.saved.crawfordPlayed;
    this.state.phase = { kind: "roll" };
    this.emit();
    this.scheduleRoll(600);
    return true;
  }

  /** Player on roll rolls dice (called for non-opening turns). */
  rollDice(): void {
    if (this.state.phase.kind !== "roll") return;
    this.state.awaitingRoll = false;
    const [d1, d2] = rollDice();
    this.state.position.dice = [d1, d2];
    this.afterRoll();
  }

  /** Resolve the current dice into legal plays, then either pick (CPU) or wait (human). */
  private afterRoll(): void {
    const dice = this.state.position.dice;
    if (!dice) return;
    // Normalize so the larger die is on the left. Single-tap auto-exec picks
    // the leftmost remaining die, so this makes the bigger die go first by
    // default — matching backgammon convention. The user can swap mid-turn.
    if (dice.length === 2 && dice[0] !== dice[1] && dice[0] < dice[1]) {
      this.state.position.dice = [dice[1], dice[0]];
    }
    const [d1, d2] = this.state.position.dice as [number, number];
    const legalPlays = generatePlays(this.state.position, d1, d2);
    this.triggerEagerAnalysis(this.state.position, legalPlays);

    const onRoll = this.state.position.turn;
    const isHuman = onRoll === 0 ? this.state.settings.whitePlayer === "human" : this.state.settings.blackPlayer === "human";

    if (legalPlays.length === 1 && legalPlays[0].length === 0) {
      // Forfeit
      this.state.phase = { kind: "play", legalPlays };
      this.state.pendingPlay = [];
      this.emit();
      setTimeout(() => this.commitPlay(), 1000);
      return;
    }

    if (isHuman) {
      this.state.phase = { kind: "play", legalPlays };
      this.state.pendingPlay = [];
      this.state.selectedFrom = null;
      this.emit();
    } else {
      this.state.phase = { kind: "cpu-thinking" };
      this.state.pendingPlay = [];
      this.emit();
      void this.ai
        .pickMove(this.state.position, legalPlays, this.state.settings.cpuDifficulty)
        .then((play) => {
          if (this.state.phase.kind !== "cpu-thinking") return;
          this.state.pendingPlay = play;
          // Emit the pending-play state so the renderer can animate each sub-move
          // before the commit-state arrives.
          this.emit();
          setTimeout(() => this.commitPlay(), 100);
        });
    }
  }

  /** User clicks a source point (or BAR=24). When `silent` is true, no emit
   * fires — useful when an immediate `pickTo` is about to emit anyway, so the
   * intermediate "source-selected, no destination yet" frame doesn't flash. */
  pickFrom(point: number, silent = false): void {
    if (this.state.phase.kind !== "play") return;
    const targets = legalNextTargets(this.state.phase.legalPlays, this.state.pendingPlay);
    if (!targets.has(point)) {
      this.state.selectedFrom = null;
      if (!silent) this.emit();
      return;
    }
    this.state.selectedFrom = point;
    if (!silent) this.emit();
  }

  clearSelection(): void {
    if (this.state.selectedFrom === null) return;
    this.state.selectedFrom = null;
    this.emit();
  }

  /** User clicks a destination point (or OFF=-1). */
  pickTo(point: number): void {
    if (this.state.phase.kind !== "play") return;
    const from = this.state.selectedFrom;
    if (from === null) {
      // Allow clicking destination directly only if there's a single legal source for that target
      const targets = legalNextTargets(this.state.phase.legalPlays, this.state.pendingPlay);
      const sources: number[] = [];
      for (const [src, dests] of targets) if (dests.has(point)) sources.push(src);
      if (sources.length === 1) {
        this.state.selectedFrom = sources[0];
      } else {
        return;
      }
    }
    const sub = this.findSub(this.state.selectedFrom!, point);
    if (!sub) {
      this.state.selectedFrom = null;
      this.emit();
      return;
    }
    this.state.pendingPlay.push(sub);
    this.state.selectedFrom = null;
    // Emit on every append so the renderer can animate this sub-move. The user
    // confirms the turn by tapping the dice (see commitPlay); no auto-commit
    // EXCEPT when this sub-move bears off the 15th checker — at that point
    // the game is decided, there's nothing to confirm, so auto-commit after a
    // short delay that lets the bear-off animation play out.
    this.emit();
    if (sub.to === OFF) {
      const after = applyPlay(this.state.position, this.state.pendingPlay);
      if (after.offUs >= CHECKERS_PER_SIDE) {
        setTimeout(() => this.commitPlay(), 250);
      }
    }
  }

  /** Swap the order of the two unused dice. No-op for doubles or if any die has been consumed. */
  swapDice(): void {
    if (this.state.phase.kind !== "play") return;
    if (this.state.pendingPlay.length !== 0) return;
    const d = this.state.position.dice;
    if (!d || d.length !== 2) return;
    if (d[0] === d[1]) return;
    this.state.position.dice = [d[1], d[0]];
    // Regenerate legal plays so the canonical "first sub-move" per source
    // reflects the new ordering (the dedup in generatePlays is order-sensitive).
    const legalPlays = generatePlays(this.state.position, d[1], d[0]);
    this.triggerEagerAnalysis(this.state.position, legalPlays);
    this.state.phase = { kind: "play", legalPlays };
    this.emit();
  }

  private findSub(from: number, to: number): SubMove | null {
    if (this.state.phase.kind !== "play") return null;
    const remaining = remainingPlays(this.state.phase.legalPlays, this.state.pendingPlay);
    for (const play of remaining) {
      if (play.length <= this.state.pendingPlay.length) continue;
      const sub = play[this.state.pendingPlay.length];
      if (sub.from === from && sub.to === to) return sub;
    }
    return null;
  }

  undo(): void {
    if (this.state.phase.kind !== "play") return;
    if (this.state.pendingPlay.length === 0) return;
    this.state.pendingPlay.pop();
    this.state.selectedFrom = null;
    this.emit();
  }

  /** Apply a full play in one shot — used by the hint modal's "play this"
   * button. The renderer animates each sub-move (the snapshot queue sees a
   * length-0 → length-N pendingPlay extension and slides each in turn), then
   * we commit after the animations have time to play.
   *
   * Any partial pending play the user had built up is discarded — the hint
   * modal's backdrop is click-through so the user may have accidentally
   * tapped checkers while it was open; their intent in clicking "Play
   * selected" is unambiguous so we just take over from a clean slate. */
  playFullPlay(play: Play): void {
    if (this.state.phase.kind !== "play") return;
    // Verify the play is among the legal options before clobbering pending.
    const legal = this.state.phase.legalPlays.some(
      (lp) => lp.length === play.length && lp.every((s, i) => s.from === play[i].from && s.to === play[i].to && s.die === play[i].die),
    );
    if (!legal) return;
    this.state.pendingPlay = [];
    this.state.selectedFrom = null;
    this.state.pendingPlay = [...play];
    this.state.selectedFrom = null;
    this.emit();
    // Schedule commit after the animations have time to play.
    const ANIM_PER_SUB = 150;
    const delay = play.length * ANIM_PER_SUB + 80;
    setTimeout(() => this.commitPlay(), delay);
  }

  /** Commit pendingPlay as the turn's final play and pass to next side.
   *
   * Caller is responsible for ensuring the pending play is a legal complete play
   * (either max-length or [] for a forfeit). Callers from within the controller
   * (CPU AI, opening forfeit) construct legal plays. The UI calls this only when
   * the user confirms via a dice tap and `isPendingComplete` is true.
   */
  commitPlay(): void {
    if (this.state.phase.kind === "play") {
      if (!isPendingComplete(this.state.phase.legalPlays, this.state.pendingPlay)) return;
    }
    let pos = applyPlay(this.state.position, this.state.pendingPlay);

    const onRoll = this.state.position.turn;
    const committedPlay = [...this.state.pendingPlay];
    const active = this.activeAnalysis;

    // Capture tutor inputs BEFORE we mutate position/phase so the analysis
    // can run after we've already moved on visibly. The analysis promise rides
    // along so endGame (which clears activeAnalysis) can't strand it.
    let tutorCtx: TutorCtx | null = null;
    if (this.state.settings.tutorMode !== "off" && active) {
      const isHuman = onRoll === 0 ? this.state.settings.whitePlayer === "human" : this.state.settings.blackPlayer === "human";
      if (isHuman) {
        tutorCtx = {
          onRoll,
          yourPlay: committedPlay,
          legalPlays: active.legalPlays,
          startPos: this.state.position,
          analysis: active.promise,
        };
      }
    }

    pos.dice = null;

    // Did this turn end the game?
    const result = checkWin(pos);
    if (result) {
      // Grade the winning move too. It's a decision like any other, and
      // silently dropping every game's last move from the report both shrank
      // the sample and biased it. `silent` keeps it out of the way: no modal
      // over the win screen, and no roll to schedule.
      if (tutorCtx) void this.runTutorAnalysis(tutorCtx, { silent: true });
      this.endGame(pos, result);
      return;
    }

    // Pass turn
    pos = mirror(pos);
    this.state.position = pos;
    this.state.pendingPlay = [];
    this.state.selectedFrom = null;

    const bothCpu =this.state.settings.whitePlayer === "cpu" && this.state.settings.blackPlayer === "cpu";

    // Cube decision: only fires in AI-vs-AI matches (no UI for human take/drop).
    // The next mover (pos.turn) may offer the cube before rolling.
    const cubeShouldFire =
      this.state.settings.cubeEnabled
      && bothCpu
      && this.state.settings.matchLength > 1
      && !this.state.position.crawford
      && canDouble(pos, pos.turn);
    if (cubeShouldFire) {
      this.state.phase = { kind: "cube-decision", doubler: pos.turn };
      this.emit();
      void this.runCubeFlow(pos.turn, tutorCtx);
      return;
    }

    this.state.phase = { kind: "roll" };
    this.emit();
    this.saveCheckpoint();
    if (tutorCtx) {
      // Record the analysis. In "tutor" mode an error/blunder keeps the dice
      // off the board until the user dismisses the modal via ackTutor(); in
      // "trainer" mode it just accrues silently toward the end-of-game report.
      void this.runTutorAnalysis(tutorCtx);
    } else {
      this.scheduleRoll(100);
    }
  }

  /**
   * The player in front of the board concedes the game in progress: the
   * opponent takes the current cube value as a single game and the normal
   * end-of-game screen follows — including the play-rating report, which was
   * previously only reachable by playing a game out to its end.
   */
  resign(): void {
    const k = this.state.phase.kind;
    if (k !== "play" && k !== "roll" && k !== "opening" && k !== "cpu-thinking" && k !== "cube-decision") {
      return;
    }
    const resigner = this.resigningSide();
    const winner: Side = (1 - resigner) as Side;
    const pos: Position = {
      ...this.state.position,
      points: Int8Array.from(this.state.position.points),
      dice: null,
    };
    this.state.pendingPlay = [];
    this.state.selectedFrom = null;
    this.endGame(pos, { winner, kind: "single", basePoints: 1 });
  }

  /** Who a resignation would come from: against the computer that's always the
   * human's side, whoever is on roll; in pass-and-play it's the side to move. */
  resigningSide(): Side {
    const s = this.state.settings;
    const humanSides: Side[] = [];
    if (s.whitePlayer === "human") humanSides.push(0);
    if (s.blackPlayer === "human") humanSides.push(1);
    return humanSides.length === 1 ? humanSides[0] : this.state.position.turn;
  }

  /** Finalize a game's end: apply stake, set phase, persist. */
  private endGame(pos: Position, result: GameResult): void {
    this.activeAnalysis = null;
    this.state.awaitingRoll = false;
    const stake = result.basePoints * pos.cube.value;
    if (result.winner === 0) this.state.whiteScore += stake;
    else this.state.blackScore += stake;
    // Crawford bookkeeping: if THIS game was the Crawford game, the next
    // games are post-Crawford and the cube is live again. Set this AFTER
    // applying the stake so startNewGame sees the up-to-date `played` flag.
    if (this.state.position.crawford) this.state.crawfordPlayed = true;
    this.state.position = pos;
    this.state.pendingPlay = [];
    const matchLen = this.state.settings.matchLength;
    const matchOver = matchLen > 1 && (this.state.whiteScore >= matchLen || this.state.blackScore >= matchLen);
    this.state.phase = matchOver
      ? { kind: "match-won", winner: this.state.whiteScore > this.state.blackScore ? 0 : 1 }
      : { kind: "won", result };
    void saveGame(null); // clear saved game on game end
    this.emit();
  }

  /** Drive an AI cube decision + (if doubled) the receiver's take/drop. */
  private async runCubeFlow(doubler: Side, tutorCtx: TutorCtx | null): Promise<void> {
    let action;
    try {
      action = await this.ai.decideCube(this.state.position, doubler);
    } catch {
      action = "no_double" as const;
    }
    // Bail if the phase has moved on (e.g. user returned to menu mid-flight).
    if (this.state.phase.kind !== "cube-decision") return;
    if (action === "no_double") {
      this.advanceToRoll(tutorCtx);
      return;
    }

    // Receiver decides. Mirror the position so the receiver's side is on
    // roll in the encoding — decideTake operates from `side`'s perspective.
    const receiver: Side = (1 - doubler) as Side;
    const receiverPos = mirror(this.state.position);
    let response;
    try {
      response = await this.ai.decideTake(receiverPos, receiver);
    } catch {
      response = "take" as const;
    }
    if (this.state.phase.kind !== "cube-decision") return;

    if (response === "drop") {
      // Doubler wins current cube value as points; game ends without rolling.
      const stakePoints = this.state.position.cube.value;
      const result: GameResult = {
        winner: doubler,
        kind: "single",
        basePoints: 1,
      };
      // Reuse endGame; pass a synthetic position with cube_value retained
      // so the standard `stake = basePoints * cube.value` math works out.
      this.endGame(this.state.position, result);
      // The standard endGame uses basePoints * cube.value = 1 * V = V, which
      // matches the drop reward. (no-op safety: stakePoints aligns.)
      void stakePoints;
      return;
    }

    // Take: cube doubles, ownership goes to receiver.
    this.state.position = applyDoubleAccepted(this.state.position, doubler);
    this.advanceToRoll(tutorCtx);
  }

  private advanceToRoll(tutorCtx: TutorCtx | null): void {
    this.state.phase = { kind: "roll" };
    this.emit();
    this.saveCheckpoint();
    if (tutorCtx) {
      void this.runTutorAnalysis(tutorCtx);
    } else {
      this.scheduleRoll(100);
    }
  }

  /**
   * Run tutor analysis for a just-committed human play and record the entry.
   * In "tutor" mode an error/blunder pauses the next dice roll until ackTutor()
   * is called (so the UI can show an analysis modal first); in every other mode
   * the roll proceeds immediately and the entry only feeds the end-of-game report.
   *
   * `silent` records the entry without flagging it or touching the dice — used
   * for the move that ends the game, where there is no next roll and no room
   * for a lesson over the win screen.
   */
  private async runTutorAnalysis(ctx: TutorCtx, opts: { silent?: boolean } = {}): Promise<void> {
    let blocked = false;
    const epoch = this.analysisEpoch;
    try {
      // Use the eager analysis triggered at the start of the turn.
      const analysis = await ctx.analysis;
      // Stale resolve from a previous game/menu: skip the history push AND
      // the tail rollDice — a dead game must neither inject a tutor entry
      // into the next game nor trigger a double roll.
      if (epoch !== this.analysisEpoch) return;
      const yourIdx = ctx.legalPlays.findIndex(
        (lp) => lp.length === ctx.yourPlay.length && lp.every((s, i) => s.from === ctx.yourPlay[i].from && s.to === ctx.yourPlay[i].to),
      );

      // Loss is measured in points equity — the metric bestEquity/bestPlay
      // are chosen by (bestEquity === max finite pointsEquities). The clamp
      // only matters for the yourIdx < 0 fallback.
      const yourEquity = yourIdx >= 0 ? analysis.pointsEquities[yourIdx] : analysis.bestEquity;
      const eqLoss = Math.max(0, analysis.bestEquity - yourEquity);
      const classification = classifyEquityLoss(eqLoss);
      const entry: TutorEntry = {
        side: ctx.onRoll,
        equityLoss: eqLoss,
        classification,
        bestPlay: analysis.bestPlay,
        yourPlay: ctx.yourPlay,
        forced: isForcedTurn(ctx.startPos, ctx.legalPlays),
        startPos: ctx.startPos,
        legalPlays: ctx.legalPlays,
        pointsEquities: analysis.pointsEquities,
        gameEquities: analysis.gameEquities,
      };
      this.state.tutor.history.push(entry);
      this.state.tutor.lastEntry = entry;
      // Only "tutor" mode interrupts play; "trainer" stays silent until the report.
      blocked =
        !opts.silent
        && this.state.settings.tutorMode === "tutor"
        && (classification === "error" || classification === "blunder");
      if (blocked) this.tutorAckPending = true;
      this.emit();
    } catch (e) {
      // ignore tutor errors
    }
    if (!opts.silent && !blocked) {
      this.scheduleRoll(100);
    }
  }

  /**
   * Called by the UI when the user dismisses the error/blunder tutor modal.
   * Resumes the next dice roll that commitPlay deferred.
   */
  ackTutor(): void {
    if (!this.tutorAckPending) return;
    this.tutorAckPending = false;
    if (this.state.phase.kind === "roll") {
      this.scheduleRoll(100);
    }
  }

  returnToMenu(): void {
    this.analysisEpoch++;
    this.activeAnalysis = null;
    this.state.currentEquity = null;
    this.state.awaitingRoll = false;
    this.state.phase = { kind: "menu" };
    void saveGame(null); // an abandoned game is not resumable
    this.emit();
  }

  shouldFlipDisplay(): boolean {
    // Position is stored in "us" (player-on-roll) perspective. shouldFlipDisplay
    // mirrors the view so that:
    //  - vs-CPU: the human always sits at the bottom.
    //  - 2P: white always sits at the bottom (fixed board orientation).
    //  - both-CPU (debug only): no flip.
    const s = this.state.settings;
    if (s.whitePlayer === "cpu" && s.blackPlayer === "cpu") return false;
    if (s.whitePlayer === "human" && s.blackPlayer === "human") {
      return this.state.position.turn === 1;
    }
    const humanSide: Side = s.whitePlayer === "human" ? 0 : 1;
    return this.state.position.turn !== humanSide;
  }
}

function classifyEquityLoss(eq: number): TutorEntry["classification"] {
  // Bands on gammon-aware points-equity loss — gnubg's EMG bands (their
  // native scale), widened a touch on the "good" end to absorb residual
  // 2-3 ply / rollout noise so a near-tie is not flagged. The book + deep
  // neural search supply the equities; these thresholds just classify the loss.
  if (eq < 0.024) return "good";
  if (eq < 0.045) return "doubtful";
  if (eq < 0.09) return "error";
  return "blunder";
}

// Sanity helper used by tests / debugging — verify total checker count.
export function checkerInvariant(p: Position): boolean {
  let us = p.barUs + p.offUs;
  let them = p.barThem + p.offThem;
  for (let i = 0; i < p.points.length; i++) {
    if (p.points[i] > 0) us += p.points[i];
    else if (p.points[i] < 0) them += -p.points[i];
  }
  return us === CHECKERS_PER_SIDE && them === CHECKERS_PER_SIDE;
}
